import { ERROR_LEVELS, ORDER_STATUS } from '../constants';
import { BuildOrderFiltersOptions, OrderFilter, OrderRow } from '../types';

/**
 * Format order data for display
 * @param order - Raw order data
 * @returns Formatted order
 */
export const formatOrderData = (order: OrderRow) => {
  return {
    ...order,
    displayId: order.partnerPo || order.internalId,
    statusText: getStatusText(order.status),
    formattedDate: formatDate(order.createdDate as string | undefined)
  };
};

/** Aggregate order-status counts produced by {@link calculateMetrics}. */
export interface OrderMetrics {
  total: number;
  pending: number;
  processing: number;
  completed: number;
  failed: number;
  cancelled: number;
}

/** Minimal shape `calculateMetrics` needs — any order-like object with a status code. */
export interface OrderStatusLike {
  status: number;
}

/**
 * Calculate order metrics
 * @param orders - Array of orders
 * @returns Order metrics
 */
export const calculateMetrics = (orders: OrderStatusLike[]): OrderMetrics => {
  const metrics: OrderMetrics = {
    total: orders.length,
    pending: 0,
    processing: 0,
    completed: 0,
    failed: 0,
    cancelled: 0
  };

  orders.forEach(order => {
    if (order.status < ORDER_STATUS.PENDING) metrics.pending++;
    else if (order.status < ORDER_STATUS.CANCELLED) metrics.processing++;
    else if (order.status === ORDER_STATUS.CANCELLED) metrics.cancelled++;
    else if (order.status === ORDER_STATUS.FAILED) metrics.failed++;
    else if (order.status >= ORDER_STATUS.COMPLETED) metrics.completed++;
  });

  return metrics;
};

/**
 * Get status text from status code
 * @param status - Status code
 * @returns Status text
 */
export const getStatusText = (status: number): string => {
  const statusMap: Record<number, string> = {
    [ORDER_STATUS.READY]: 'Ready',
    [ORDER_STATUS.PENDING]: 'Pending',
    [ORDER_STATUS.CANCELLED]: 'Cancelled',
    [ORDER_STATUS.FAILED]: 'Failed',
    [ORDER_STATUS.COMPLETED]: 'Completed',
    [ORDER_STATUS.IGNORED]: 'Ignored'
  };
  return statusMap[status] || 'Unknown';
};

/**
 * Get status color
 * @param status - Status code
 * @returns Color code
 */
export const getStatusColor = (status: number): string => {
  if (status < ORDER_STATUS.PENDING) return '#FFA500'; // Pending - Orange
  if (status === ORDER_STATUS.CANCELLED) return '#AAA';   // Cancelled - Gray
  if (status === ORDER_STATUS.FAILED) return '#EE3224'; // Failed - Red
  if (status >= ORDER_STATUS.COMPLETED) return '#448044'; // Completed - Green
  return '#000'; // Default - Black
};

/**
 * Map error level
 * @param level - Error level number
 * @returns Error level text
 */
export const mapErrorLevel = (level: number): string => {
  return ERROR_LEVELS[level] || ERROR_LEVELS[4];
};

/**
 * Format date for display
 * @param date - ISO date string
 * @returns Formatted date
 */
export const formatDate = (date: string | null | undefined): string => {
  if (!date) return '-';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString();
};

/**
 * Format currency
 * @param amount - Amount in cents
 * @returns Formatted currency
 */
export const formatCurrency = (amount: number | null | undefined): string => {
  if (amount == null) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount / 100);
};

/**
 * Get company names from order
 * @param order - Order object
 * @returns Company names
 */
export const getOrderCompanyNames = (order: OrderRow) => {
  return {
    supplier: (order.supplierCompanyName as string | undefined) || '-',
    retailer: (order.retailerCompanyName as string | undefined) || '-',
    partner: order.partnerCompanyName || '-'
  };
};

/**
 * Build filter array for orders
 * @param options - Filter options
 * @returns Filter array
 */
export const buildOrderFilters = (options: BuildOrderFiltersOptions = {}): OrderFilter[] => {
  const filters: OrderFilter[] = [
    { field: "documentType", operator: "eq", value: "Order", system: true }
  ];

  if (options.hideSalesOrders && options.companyName) {
    filters.push({
      field: "partnerCompanyName",
      operator: "ne",
      value: options.companyName,
      system: true
    });
  }

  if (options.status) {
    if (isNaN(options.status as number)) {
      filters.push({
        field: "statusDescription",
        operator: "eq",
        value: options.status
      });
    } else {
      filters.push({
        field: "status",
        operator: "eq",
        value: parseInt(options.status as string)
      });
    }
  }

  if (options.statusMax) {
    filters.push({
      field: "status",
      operator: "le",
      value: parseInt(options.statusMax as string)
    });
  }

  if (options.statusCode) {
    filters.push({
      field: "status",
      operator: "eq",
      value: parseInt(options.statusCode as string)
    });
  }

  return filters;
};

/**
 * Get query parameters from URL
 * @returns Query parameters
 */
export const getQueryParams = (): Record<string, string> => {
  const params = new URLSearchParams(window.location.search);
  const result: Record<string, string> = {};

  // Use forEach rather than for...of — iterating URLSearchParams directly
  // requires downlevelIteration under this project's es5 compile target.
  params.forEach((value, key) => {
    result[key] = value;
  });

  return result;
};